import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import type { GetPricelistResponse } from '@erp/rpc';
import type { PricelistReceivedAs } from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import { invalidatePricelistCaseQueries } from './pricelistCaseQueryInvalidation';
import { useCatalogDivisions } from '@/api/rpc/hooks/inventory-management/useItemCatalog';
import { useToast } from 'src/context/ToastContext';
import {
  buildDetailsPayload,
  invalidPercentKeys,
  valuesEqual,
  valuesFromDetails,
  type DivisionOption,
  type PricelistDetailValues,
  type PricelistDetailsFormValues,
} from './pricelistDetailsFormState';

interface FormState {
  /** The saved details these values were loaded from. */
  source: PricelistDetailValues;
  values: PricelistDetailsFormValues;
}

/** What the payload looks like once the division list has been applied. */
export type PricelistDetailsPayload = ReturnType<typeof buildDetailsPayload>;

/**
 * The record the form reads and writes — a FILE's legacy details row, or a
 * pricelist update case.
 *
 * The form itself is identical for both (same nine fields, same division
 * table, same validation); only where the values come from and where Save
 * sends them differ. Parameterising on those two things is what keeps one
 * implementation instead of a card per record type, which would have drifted
 * the moment either gained a field.
 */
export interface PricelistDetailsTarget {
  /**
   * The saved values. Its IDENTITY is the reload signal: a fresh object from
   * a new fetch replaces the form's copy, so a save elsewhere is not silently
   * overwritten by stale inputs.
   */
  details: PricelistDetailValues;
  isSaving: boolean;
  /** Persist the payload. Throwing surfaces the error toast. */
  save: (payload: PricelistDetailsPayload) => Promise<void>;
}

export interface PricelistDetailsForm {
  /** The catalog's divisions (DataSources), in group/source order. */
  divisions: DivisionOption[];
  /**
   * False until the division list has actually loaded. Saving must wait for
   * it: the payload keeps only the divisions on offer, so a save against the
   * empty placeholder list would wipe every saved division percentage.
   */
  divisionsReady: boolean;
  values: PricelistDetailsFormValues;
  setEntryPercent: (entryKey: string, percent: string) => void;
  /**
   * Set the same percentage on many entries at once — the table's bulk apply,
   * and the only correct way to write more than one: a percentage per call
   * would rebuild the map from a stale render's copy and keep only the last.
   * Blank clears them, which is how a division is taken off the saved details.
   */
  setEntryPercents: (entryKeys: string[], percent: string) => void;
  setAnticipatedPercent: (percent: string) => void;
  setFromTariffChange: (value: boolean) => void;
  /** YYYY-MM-DD, or '' to clear. */
  setEffectiveDate: (date: string) => void;
  /** YYYY-MM-DD, or '' to clear. */
  setErpUploadDate: (date: string) => void;
  setReceivedAs: (receivedAs: PricelistReceivedAs | '') => void;
  /** YYYY-MM-DD, or '' to clear. */
  setReceivedDate: (date: string) => void;
  setNotes: (notes: string) => void;
  /** Field keys (entry key, or 'anticipated') holding an invalid percentage. */
  invalidKeys: string[];
  isDirty: boolean;
  isSaving: boolean;
  /** True while the card shows inputs rather than saved values. */
  isEditing: boolean;
  startEditing: () => void;
  save: () => void;
  /** Drop any edits and leave edit mode. */
  cancelEditing: () => void;
}

/**
 * Form state for the manually maintained pricelist details — the per-division
 * price change, the tariff flag, the anticipated vendor change, and the notes.
 * The details card reads the saved values until the pencil switches it into
 * edit mode; one Save then covers every field.
 *
 * Record-agnostic: {@link usePricelistDetailsForm} binds it to a FILE's legacy
 * details row and {@link usePricelistCaseDetailsForm} to a case.
 */
export function useDetailsFormState(
  target: PricelistDetailsTarget,
): PricelistDetailsForm {
  const gt = useGT();
  const { showToast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  // The catalog's division list, not the org's full DataSource list: other
  // feeds (the GAINS ingest) mirror the same division titles under their own
  // group, which would list every division twice here.
  const { data: divisionsData } = useCatalogDivisions();
  const divisions: DivisionOption[] = divisionsData?.divisions ?? [];
  // Undefined while loading *and* on error — either way the fallback [] above
  // is a placeholder, not the real list, and must never back a save.
  const divisionsReady = divisionsData !== undefined;

  // Reloaded during render whenever a fresh fetch lands ("adjust state when
  // props change"), so saved values replace the form's copy of them.
  const [state, setState] = useState<FormState>(() => ({
    source: target.details,
    values: valuesFromDetails(target.details),
  }));
  if (state.source !== target.details) {
    setState({
      source: target.details,
      values: valuesFromDetails(target.details),
    });
  }

  function patch(values: Partial<PricelistDetailsFormValues>) {
    setState((prev) => ({ ...prev, values: { ...prev.values, ...values } }));
  }

  async function submit() {
    // Belt to the Save button's braces: never write a payload built from the
    // placeholder division list.
    if (!divisionsReady) return;
    try {
      await target.save(buildDetailsPayload(state.values, divisions));
      setIsEditing(false);
      showToast({
        title: gt('Saved', {
          context: 'toast title when pricelist details are saved',
        }),
        message: gt('The pricelist details were updated.', {
          context: 'toast message when pricelist details are saved',
        }),
        color: 'success',
      });
    } catch {
      showToast({
        title: gt('Error', { context: 'toast title for error state' }),
        message: gt('Failed to save the pricelist details.', {
          context: 'toast message when saving pricelist details fails',
        }),
        color: 'error',
      });
    }
  }

  function setEntryPercents(entryKeys: string[], percent: string) {
    setState((prev) => {
      const percentByEntryKey = { ...prev.values.percentByEntryKey };
      for (const key of entryKeys) percentByEntryKey[key] = percent;
      return { ...prev, values: { ...prev.values, percentByEntryKey } };
    });
  }

  return {
    divisions,
    divisionsReady,
    values: state.values,
    setEntryPercent: (entryKey, percent) =>
      setEntryPercents([entryKey], percent),
    setEntryPercents,
    setAnticipatedPercent: (percent) => patch({ anticipatedPercent: percent }),
    setFromTariffChange: (value) => patch({ fromTariffChange: value }),
    setEffectiveDate: (date) => patch({ effectiveDate: date }),
    setErpUploadDate: (date) => patch({ erpUploadDate: date }),
    setReceivedAs: (receivedAs) => patch({ receivedAs }),
    setReceivedDate: (date) => patch({ receivedDate: date }),
    setNotes: (notes) => patch({ notes }),
    invalidKeys: invalidPercentKeys(state.values),
    isDirty: !valuesEqual(state.values, valuesFromDetails(state.source)),
    isSaving: target.isSaving,
    isEditing,
    startEditing: () => setIsEditing(true),
    save: () => void submit(),
    cancelEditing: () => {
      setState((prev) => ({
        ...prev,
        values: valuesFromDetails(prev.source),
      }));
      setIsEditing(false);
    },
  };
}

/**
 * The form bound to ONE FILE's legacy `im_pricelist_details` row.
 *
 * Still the name the file detail card imports, so nothing about the legacy
 * path changed. Note the backend refuses this write once the document belongs
 * to a case — the case then owns the domain, and the card renders read-only.
 */
export function usePricelistDetailsForm(
  pricelist: GetPricelistResponse,
): PricelistDetailsForm {
  const queryClient = useQueryClient();
  const updateMutation = useMutation(
    orpc.inventoryManagement.pricelist.updateDetails.mutationOptions(),
  );

  return useDetailsFormState({
    details: pricelist.details,
    isSaving: updateMutation.isPending,
    save: async (payload) => {
      await updateMutation.mutateAsync({
        pdfDocumentId: pricelist.id,
        ...payload,
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.pricelist.get.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.pricelist.listEvents.key(),
      });
    },
  });
}

/**
 * The same form bound to a PRICELIST UPDATE CASE.
 *
 * Saving goes to `cases.update`, whose write the `audit_write_change_log`
 * trigger turns into a per-field diff — which is why the case timeline is
 * invalidated alongside the case itself: the save is what produced the new
 * timeline entry.
 */
export function usePricelistCaseDetailsForm(
  caseId: string,
  details: PricelistDetailValues,
): PricelistDetailsForm {
  const queryClient = useQueryClient();
  const updateMutation = useMutation(
    orpc.inventoryManagement.pricelistCases.update.mutationOptions(),
  );

  return useDetailsFormState({
    details,
    isSaving: updateMutation.isPending,
    save: async (payload) => {
      await updateMutation.mutateAsync({ caseId, details: payload });
      invalidatePricelistCaseQueries(queryClient);
    },
  });
}
